import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import { User, Mail, Phone, Calendar, Clock, Lock, Eye, EyeOff, Shield, CheckCircle, Pencil, KeyRound, Upload, X, Camera } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { formatPhoneNumberWithCountryCode, formatPhoneNumber, unformatPhoneNumber } from '../utils/phoneFormatter';
import { handleApiError } from '../utils/errorHandler';
import appConfig from '../config/appConfig';
import Spinner from '../components/Spinner';
import ImageEditor from '../components/ImageEditor';
import OtpVerifyOverlay from '../components/OtpVerifyOverlay';
import NotificationPreferencesCard from '../components/profile/NotificationPreferencesCard';
import AccountTabs, { useAccountTab } from '../components/profile/AccountTabs';
import '../styles/Profile.css';
import '../styles/Auth.css';
import logger from '../utils/logger';

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // Check if it's already a complete URL (starts with http:// or https://)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Otherwise, it's a relative path, so prepend the origin
  return `${window.location.origin}${imagePath}`;
};

const Profile = () => {
  // My Account = one page, three tabs (hash-driven: /profile#notifications)
  const [accountTab, setAccountTab] = useAccountTab();
  const { user, checkAuth } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
  });
  const [mobileNumber, setMobileNumber] = useState(''); // For formatted display in edit mode
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [aiAssistantImage, setAiAssistantImage] = useState(null);
  const [aiAssistantImagePreview, setAiAssistantImagePreview] = useState(null);
  const [showProfileImageEditor, setShowProfileImageEditor] = useState(false);
  const [, setShowAiImageEditor] = useState(false);
  const [editingServerImage, setEditingServerImage] = useState(false);
  const [savingProfileImage, setSavingProfileImage] = useState(false);
  // OTP gate state for phone changes (phone-verification spec §5).
  // When the user submits a different phone, we open the overlay and wait
  // for verification before calling PUT /users/profile.
  const [otpGatePhone, setOtpGatePhone] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
      });
      
      // Extract mobile number from full phone number for formatting
      if (user.phone_number) {
        const cleaned = user.phone_number.replace(/\D/g, '');
        // Remove country code (1) if present
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
          setMobileNumber(cleaned.substring(1));
        } else if (cleaned.length === 10) {
          setMobileNumber(cleaned);
        } else {
          setMobileNumber(cleaned);
        }
      }
      
      // Set existing image previews if available
      if (user.user_image) {
        setProfileImagePreview(getImageUrl(user.user_image));
      }
      if (user.ai_assistant_image) {
        setAiAssistantImagePreview(getImageUrl(user.ai_assistant_image));
      }
      setInitialLoading(false);
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type using config
      if (!appConfig.upload.allowedImageTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, or BMP)');
        return;
      }

      // Enhanced security: Check file extension matches MIME type using config
      const fileExtension = file.name.toLowerCase().split('.').pop();
      if (!appConfig.upload.allowedImageExtensions.includes(fileExtension)) {
        toast.error('File extension not allowed. Please use: ' + appConfig.upload.allowedImageExtensions.join(', '));
        return;
      }

      // Validate file size using config (default: 20MB)
      const maxSize = appConfig.getMaxImageSizeBytes();
      if (file.size > maxSize) {
        toast.error(`Image size should not exceed ${appConfig.getMaxImageSizeText()}`);
        return;
      }

      // Enhanced security: Check minimum file size using config
      if (file.size < appConfig.upload.minImageSizeBytes) {
        toast.error('File is too small to be a valid image');
        return;
      }

      // Enhanced security: Check file name for suspicious patterns
      const suspiciousPatterns = ['.php', '.exe', '.js', '.html', '.svg', '..', '<', '>', '|'];
      const fileName = file.name.toLowerCase();
      for (const pattern of suspiciousPatterns) {
        if (fileName.includes(pattern)) {
          toast.error('File name contains suspicious characters');
          return;
        }
      }

      // Create preview and save directly - Don't open editor automatically
      const reader = new FileReader();
      reader.onloadend = () => {
        if (imageType === 'profile') {
          setProfileImage(file); // Save file directly
          setProfileImagePreview(reader.result); // Show preview
          // Don't open editor - let user click Edit button if they want
        } else {
          setAiAssistantImage(file); // Save file directly
          setAiAssistantImagePreview(reader.result); // Show preview
          // Don't open editor - let user click Edit button if they want
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageEditorSave = async (file, dataUrl, imageType) => {
    // Editing the current server-saved profile image → upload immediately
    // (mirrors admin Edit Image flow). Fresh-upload edits still defer to form submit.
    if (imageType === 'profile' && editingServerImage) {
      try {
        setSavingProfileImage(true);
        await userAPI.updateProfile({}, file, null);
        if (checkAuth) await checkAuth();
        toast.success('Profile image updated');
        setProfileImage(null);
        setShowProfileImageEditor(false);
        setEditingServerImage(false);
      } catch (error) {
        handleApiError(error, 'PROFILE', 'Failed to update profile image');
      } finally {
        setSavingProfileImage(false);
      }
      return;
    }

    if (imageType === 'profile') {
      setProfileImage(file);
      setProfileImagePreview(dataUrl);
      setShowProfileImageEditor(false);
    } else {
      setAiAssistantImage(file);
      setAiAssistantImagePreview(dataUrl);
      setShowAiImageEditor(false);
    }
  };

  const handleImageEditorCancel = (imageType) => {
    if (imageType === 'profile') {
      if (!profileImage) {
        setProfileImagePreview(user?.user_image ? getImageUrl(user.user_image) : null);
      }
      setShowProfileImageEditor(false);
      setEditingServerImage(false);
    } else {
      if (!aiAssistantImage) {
        setAiAssistantImagePreview(user?.ai_assistant_image ? getImageUrl(user.ai_assistant_image) : null);
      }
      setShowAiImageEditor(false);
    }
  };

  const removeImage = (imageType) => {
    if (imageType === 'profile') {
      setProfileImage(null);
      if (user?.user_image) {
        setProfileImagePreview(getImageUrl(user.user_image));
      } else {
        setProfileImagePreview(null);
      }
      setShowProfileImageEditor(false);
    } else {
      setAiAssistantImage(null);
      if (user?.ai_assistant_image) {
        setAiAssistantImagePreview(getImageUrl(user.ai_assistant_image));
      } else {
        setAiAssistantImagePreview(null);
      }
      setShowAiImageEditor(false);
    }
  };

  const handleEditExistingImage = (imageType) => {
    const preview = imageType === 'profile' ? profileImagePreview : aiAssistantImagePreview;
    if (!preview) return;

    // Server-stored images are now editable via the fetch+blob path in ImageEditor.
    // Flag the server-image case so onSave knows to upload immediately.
    const isServerImage = !preview.startsWith('data:');
    if (imageType === 'profile') {
      setEditingServerImage(isServerImage);
      setShowProfileImageEditor(true);
    } else {
      setShowAiImageEditor(true);
    }
  };

  // Normalize a phone string to bare 10 digits (US). Treats '+1', '+1 ', '' as empty.
  // Used to decide whether the form's phone actually differs from what's on file.
  const normalizePhoneDigits = (value) => {
    if (!value) return '';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    return digits.length === 10 ? digits : '';
  };

  // Open OTP gate before saving when the user changed phone to a new value.
  // Empty/unchanged phone → save directly. Clearing phone is allowed without OTP
  // (spec §5 step 3).
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newDigits = normalizePhoneDigits(formData.phone_number);
    const currentDigits = normalizePhoneDigits(user?.phone_number);
    const phoneChangedToNewValue = newDigits && newDigits !== currentDigits;

    if (phoneChangedToNewValue) {
      setOtpGatePhone(`+1${newDigits}`);
      return;
    }

    // Clearing phone or unchanged → submit directly. If user typed only '+1',
    // normalize the form value to '' so backend receives a real empty string.
    if (!newDigits && formData.phone_number !== '') {
      setFormData(prev => ({ ...prev, phone_number: '' }));
    }
    await performProfileSave();
  };

  const handleOtpVerified = async () => {
    setOtpGatePhone(null);
    await performProfileSave();
  };

  const performProfileSave = async () => {
    setLoading(true);

    try {
      // Enhanced debugging
      if (process.env.NODE_ENV !== 'production') {
        logger.debug('PROFILE', '=== PROFILE UPDATE START ===');
        logger.debug('PROFILE', 'Current formData:', JSON.stringify(formData, null, 2));
        logger.debug('PROFILE', 'Has profile image:', !!profileImage);
        logger.debug('PROFILE', 'Has AI assistant image:', !!aiAssistantImage);
        logger.debug('PROFILE', 'Current user data:', JSON.stringify(user, null, 2));
      }


      const response = await userAPI.updateProfile(formData, profileImage, aiAssistantImage);

      if (process.env.NODE_ENV !== 'production') {
        logger.debug('PROFILE', '=== UPDATE RESPONSE ===');
        logger.debug('PROFILE', 'Response:', JSON.stringify(response, null, 2));
        logger.debug('PROFILE', 'Response contains:', {
          hasData: !!response,
          firstName: response?.first_name,
          lastName: response?.last_name,
          phone: response?.phone_number,
          status: response?.status,
          message: response?.message
        });
      }
      
      // Check if backend actually saved the changes
      if (response?.message || response?.status === 'success') {
        toast.success(response?.message || 'Profile updated successfully');
      } else {
        toast.success('Profile update request sent successfully');
      }
      
      // Update user data without reloading the page
      try {
        // Use userAPI.getProfile() which uses the correct /users/profile endpoint

logger.debug('PROFILE', 'Refreshing user data...');
        const updatedProfile = await userAPI.getProfile();

logger.debug('PROFILE', 'Got updated profile:', {
            firstName: updatedProfile?.first_name,
            lastName: updatedProfile?.last_name,
            phone: updatedProfile?.phone_number
          });

        // Now call checkAuth to update the global user state
        if (checkAuth) {
          await checkAuth(); // This will update the global user state

logger.debug('PROFILE', 'Global user state updated');
        }

        // Verify the update

logger.debug('PROFILE', 'Verifying update...');
        const verifyResponse = await userAPI.getProfile();
        if (process.env.NODE_ENV !== 'production') {
          logger.debug('PROFILE', 'Verification response:', {
            firstName: verifyResponse?.first_name,
            lastName: verifyResponse?.last_name,
            phone: verifyResponse?.phone_number
          });

          // Check if values actually changed
          if (verifyResponse?.first_name === formData.first_name &&
              verifyResponse?.last_name === formData.last_name &&
              verifyResponse?.phone_number === formData.phone_number) {
            logger.debug('PROFILE', '✅ Profile successfully updated and verified');
          } else {
            logger.warn('PROFILE', '⚠️ Profile may not have been updated on backend');
            logger.debug('PROFILE', 'Expected:', formData);
            logger.debug('PROFILE', 'Actual:', {
              first_name: verifyResponse?.first_name,
              last_name: verifyResponse?.last_name,
              phone_number: verifyResponse?.phone_number
            });
          }
        }
      } catch (refreshError) {

logger.error('PROFILE', 'Error refreshing user data:', refreshError);
        // User data will still be updated on next page visit
      }
      
      // Clear file inputs
      setProfileImage(null);
      setAiAssistantImage(null);
      
      // Exit edit mode AFTER updating user data
      setIsEditing(false);
    } catch (error) {
      // Backend returns 400 PHONE_NOT_VERIFIED when the FE somehow tries to
      // change the phone without OTP (e.g., race after the user's current
      // phone changed in another tab). Re-open the gate. Spec §5.
      const data = error?.response?.data;
      const code = data?.error || data?.detail?.error;
      if (error?.response?.status === 400 && code === 'PHONE_NOT_VERIFIED') {
        const digits = normalizePhoneDigits(formData.phone_number);
        if (digits) {
          setOtpGatePhone(`+1${digits}`);
          return;
        }
      }
      handleApiError(error, 'PROFILE', 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await userAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password updated successfully');
      setIsChangingPassword(false);
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      handleApiError(error, 'PROFILE', 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      approved: 'badge-success',
      pending: 'badge-warning',
      rejected: 'badge-danger',
      blocked: 'badge-danger',
    };

    return (
      <span className={`badge ${statusStyles[status] || 'badge-secondary'}`}>
        {status}
      </span>
    );
  };

  if (initialLoading || !user) {
    return (
      <div className="profile-container">
        <div className="card">
          <Spinner size="md" centered text="Loading profile..." />
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1 className="profile-title">My Account</h1>
        <p className="profile-subtitle">Manage your profile, security and notification settings</p>
      </div>

      <AccountTabs value={accountTab} onChange={setAccountTab} />

      <div className="profile-grid account-grid">
        {accountTab === 'profile' && (
        <div className="card card-animate profile-info-card" role="tabpanel" id="account-panel-profile" aria-labelledby="account-tab-profile">
          <div className="card-header">
            <div className="card-title-wrapper">
              <User size={24} className="card-icon" aria-hidden="true" />
              <h2 className="card-title">Profile Information</h2>
            </div>
            {!isEditing && (
              <button
                onClick={() => {
                  setIsEditing(true);
                  // Reset mobile number when entering edit mode
                  if (user?.phone_number) {
                    const cleaned = user.phone_number.replace(/\D/g, '');
                    if (cleaned.length === 11 && cleaned.startsWith('1')) {
                      setMobileNumber(cleaned.substring(1));
                    } else if (cleaned.length === 10) {
                      setMobileNumber(cleaned);
                    } else {
                      setMobileNumber(cleaned);
                    }
                  }
                }}
                className="btn btn-primary btn-with-icon"
                aria-label="Edit profile information"
              >
                <Pencil size={18} aria-hidden="true" />
                <span>Edit Profile</span>
              </button>
            )}
          </div>

        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                <User size={16} />
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                className="form-input"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <User size={16} />
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                className="form-input"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone_number" className="form-label">
                <Phone size={16} />
                Phone Number
              </label>
              <div className="phone-input-group" style={{ display: 'flex', alignItems: 'center' }}>
                <span className="country-code" style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.375rem 0 0 0.375rem',
                  color: 'var(--text-primary)',
                  fontWeight: '500'
                }}>
                  +1
                </span>
                <input
                  id="phone_number"
                  type="tel"
                  name="mobile_number"
                  className="form-input"
                  style={{ 
                    borderRadius: '0 0.375rem 0.375rem 0',
                    borderLeft: 'none'
                  }}
                  value={formatPhoneNumber(mobileNumber)}
                  onChange={(e) => {
                    const rawNumber = unformatPhoneNumber(e.target.value);
                    // Limit to exactly 10 digits
                    const limitedNumber = rawNumber.slice(0, 10);
                    setMobileNumber(limitedNumber);
                    // Update formData with full phone number including country code
                    setFormData({
                      ...formData,
                      phone_number: `+1${limitedNumber}`
                    });
                  }}
                  placeholder="(XXX) XXX-XXXX"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <Mail size={16} />
                Email (Cannot be changed)
              </label>
              <input
                id="email"
                type="email"
                name="email"
                className="form-input"
                value={user?.email || ''}
                readOnly
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <Camera size={16} />
                  Profile Image
                </label>
                <div className="image-upload-container">
                  {showProfileImageEditor ? (
                    <ImageEditor
                      imageUrl={profileImagePreview}
                      onSave={(file, dataUrl) => handleImageEditorSave(file, dataUrl, 'profile')}
                      onCancel={() => handleImageEditorCancel('profile')}
                      size={150}
                      saving={savingProfileImage}
                    />
                  ) : profileImagePreview ? (
                    <div className="image-preview-wrapper">
                      <img src={profileImagePreview} alt="Profile" className="image-preview" />
                      <button
                        type="button"
                        onClick={() => removeImage('profile')}
                        className="remove-image-btn"
                      >
                        <X size={16} />
                      </button>
                      <input
                        type="file"
                        id="profileImageUpload"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'profile')}
                        className="hidden-file-input"
                      />
                      <div className="image-action-buttons">
                        <button
                          type="button"
                          onClick={() => document.getElementById('profileImageUpload').click()}
                          className="change-image-btn"
                        >
                          <Camera size={16} />
                          Change
                        </button>
                        {profileImagePreview && (
                          <button
                            type="button"
                            onClick={() => handleEditExistingImage('profile')}
                            className="edit-image-btn"
                            disabled={savingProfileImage}
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="image-upload-box">
                      <input
                        type="file"
                        id="profileImageUpload"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'profile')}
                        className="hidden-file-input"
                      />
                      <label htmlFor="profileImageUpload" className="upload-label">
                        <Upload size={24} />
                        <span>Upload Profile Image</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="form-group">
                <label className="form-label">
                  <Bot size={16} />
                  AI Assistant Image
                </label>
                <div className="image-upload-container">
                  {aiAssistantImagePreview ? (
                    <div className="image-preview-wrapper">
                      <img src={aiAssistantImagePreview} alt="AI Assistant" className="image-preview" />
                      <button
                        type="button"
                        onClick={() => removeImage('ai')}
                        className="remove-image-btn"
                      >
                        <X size={16} />
                      </button>
                      <input
                        type="file"
                        id="aiImageUpload"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
                        onChange={(e) => handleImageChange(e, 'ai')}
                        className="hidden-file-input"
                      />
                      <label htmlFor="aiImageUpload" className="change-image-btn">
                        <Bot size={16} />
                        Change
                      </label>
                    </div>
                  ) : (
                    <div className="image-upload-box">
                      <input
                        type="file"
                        id="aiImageUpload"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
                        onChange={(e) => handleImageChange(e, 'ai')}
                        className="hidden-file-input"
                      />
                      <label htmlFor="aiImageUpload" className="upload-label">
                        <Upload size={24} />
                        <span>Upload AI Avatar</span>
                      </label>
                    </div>
                  )}
                </div>
              </div> */}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || showProfileImageEditor}
                title={showProfileImageEditor ? "Please apply or cancel image changes first" : ""}
              >
                {loading ? 'Saving...' : showProfileImageEditor ? 'Finish editing first...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  // Reset form data to original values
                  if (user) {
                    setFormData({
                      first_name: user.first_name || '',
                      last_name: user.last_name || '',
                      phone_number: user.phone_number || '',
                    });
                    // Reset mobile number
                    if (user.phone_number) {
                      const cleaned = user.phone_number.replace(/\D/g, '');
                      if (cleaned.length === 11 && cleaned.startsWith('1')) {
                        setMobileNumber(cleaned.substring(1));
                      } else if (cleaned.length === 10) {
                        setMobileNumber(cleaned);
                      } else {
                        setMobileNumber(cleaned);
                      }
                    }
                  }
                }}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-info">
            <div className="info-item">
              <div className="info-label">
                <User size={16} />
                Full Name
              </div>
              <div className="info-value">
                {user.first_name} {user.last_name}
              </div>
            </div>

            <div className="info-item">
              <div className="info-label">
                <Mail size={16} />
                Email
              </div>
              <div className="info-value">{user.email}</div>
            </div>

            <div className="info-item">
              <div className="info-label">
                <Phone size={16} />
                Phone
              </div>
              <div className="info-value phone-value">
                {user.phone_number ? (
                  formatPhoneNumberWithCountryCode(user.phone_number)
                ) : (
                  <span className="not-added">Not added</span>
                )}
              </div>
            </div>

            <div className="info-item">
              <div className="info-label">
                <CheckCircle size={16} />
                Status
              </div>
              <div className="info-value">
                {getStatusBadge(user.status)}
              </div>
            </div>

            <div className="profile-images-display">
              <div className="image-display-item">
                <div className="info-label">
                  <Camera size={16} />
                  Profile Image
                </div>
                {profileImagePreview ? (
                  <img src={profileImagePreview} alt="Profile" className="profile-image-display" />
                ) : (
                  <div className="no-image">
                    <span className="not-added">Not added</span>
                  </div>
                )}
              </div>
              
              {/* <div className="image-display-item">
                <div className="info-label">
                  <Bot size={16} />
                  AI Assistant Image
                </div>
                {aiAssistantImagePreview ? (
                  <img src={aiAssistantImagePreview} alt="AI Assistant" className="profile-image-display" />
                ) : (
                  <div className="no-image">No AI avatar</div>
                )}
              </div> */}
            </div>

            {user.registration_date && (
              <div className="info-item">
                <div className="info-label">
                  <Calendar size={16} />
                  Member Since
                </div>
                <div className="info-value">
                  {format(new Date(user.registration_date), 'MMMM d, yyyy')}
                </div>
              </div>
            )}

            {user.last_login && (
              <div className="info-item">
                <div className="info-label">
                  <Clock size={16} />
                  Last Login
                </div>
                <div className="info-value">
                  {format(new Date(user.last_login), 'PPpp')}
                </div>
              </div>
            )}

          </div>
        )}
        </div>
        )}

        {/* Password Change Section.
            This was gated on user?.role === 'super_admin', which hid a member's
            OWN password change from them. PUT /users/change-password has only
            ever required a login — changing your own password is not an admin
            capability. */}
        {accountTab === 'security' && (
        <div className="card card-animate security-card" role="tabpanel" id="account-panel-security" aria-labelledby="account-tab-security">
          <div className="card-header">
            <div className="card-title-wrapper">
              <Shield size={24} className="card-icon" />
              <h2 className="card-title">Security Settings</h2>
            </div>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="btn btn-primary btn-with-icon"
              >
                <KeyRound size={18} />
                <span>Change Password</span>
              </button>
            )}
          </div>

        {isChangingPassword && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label className="form-label">
                <Lock size={16} />
                Current Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showOldPassword ? "text" : "password"}
                  name="old_password"
                  className="form-input"
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Lock size={16} />
                New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="new_password"
                  className="form-input"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Lock size={16} />
                Confirm New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  className="form-input"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({
                    old_password: '',
                    new_password: '',
                    confirm_password: '',
                  });
                }}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        
        {!isChangingPassword && (
          <div className="password-info">
            <div className="security-tip">
              <Lock size={20} />
              <div>
                <h4>Password Security</h4>
                <p>Keep your account secure by using a strong password and changing it regularly.</p>
              </div>
            </div>
            <div className="security-checklist">
              <h5>Security Best Practices:</h5>
              <ul>
                <li><CheckCircle size={14} /> Use at least 8 characters</li>
                <li><CheckCircle size={14} /> Include numbers and symbols</li>
                <li><CheckCircle size={14} /> Avoid common passwords</li>
                <li><CheckCircle size={14} /> Update every 90 days</li>
              </ul>
            </div>
          </div>
        )}
        </div>
        )}

        {/* Proactive message switches (Web / SMS per type). Deep link: /profile#notifications */}
        {accountTab === 'notifications' && (
        <div role="tabpanel" id="account-panel-notifications" aria-labelledby="account-tab-notifications">
          <NotificationPreferencesCard
            id="notifications"
            load={userAPI.getNotificationPreferences}
            save={userAPI.updateNotificationPreferences}
            onSmsToggle={(enabled) =>
              userAPI.updateNotificationPreferences({}, enabled).then((d) => d?.sms)
            }
          />
        </div>
        )}
      </div>

      {otpGatePhone && (
        <OtpVerifyOverlay
          phoneE164={otpGatePhone}
          onVerified={handleOtpVerified}
          onCancel={() => setOtpGatePhone(null)}
        />
      )}
    </div>
  );
};

export default Profile;